import type { ContentAST, HeadingBlock } from './astTypes'
import { getBlockText } from './astUtils'
import { analyzeWritingSyntax, parseSectionOpening } from './writingSyntax'
import { ratingChildTagId } from '../tags/canonicalRatingTags'

export interface RatingAnnotation {
  id: string
  tags: string[]
  anchor: { type: string; blockId?: string }
  createdAt: string | Date
}
const readinessIds = [1, 2, 3, 4, 5].map((n) => ratingChildTagId('readiness', n))
export function readinessFromTags(tags: string[]): number | undefined {
  const values = tags.map((t) => readinessIds.indexOf(t) + 1).filter(Boolean)
  return values.length ? Math.max(...values) : undefined
}

/** The prefix is the stored rating; existing rating annotations are its projection for all tag UIs. */
export function syncWritingReadiness<T extends RatingAnnotation>(ast: ContentAST, annotations: T[]): T[] {
  let out = annotations.map((a) => ({ ...a, tags: [...a.tags] }))
  const byBlock = new Map<string, typeof out>()
  for (const annotation of out) {
    if (annotation.anchor.type === 'block' && annotation.anchor.blockId) {
      const id = annotation.anchor.blockId
      const matches = byBlock.get(id) ?? []
      matches.push(annotation)
      byBlock.set(id, matches)
    }
  }
  for (const { heading } of analyzeWritingSyntax(ast.blocks).sections) {
    const readiness = parseSectionOpening(getBlockText(heading))?.readiness
    const matches = byBlock.get(heading.id) ?? []
    matches.forEach((a) => {
      a.tags = a.tags.filter((t) => !readinessIds.includes(t))
    })
    if (readiness) {
      let target = matches.find((a) => a.id === `sectionTags_${heading.id}`) ?? matches[0]
      if (!target) {
        target = {
          id: `sectionTags_${heading.id}`,
          tags: [],
          anchor: { type: 'block', blockId: heading.id },
          createdAt: new Date(0).toISOString(), // Derived metadata: keep reads and revision hashes deterministic.
        } as unknown as T
        out.push(target)
      }
      target.tags.push(ratingChildTagId('readiness', readiness))
    }
  }
  out = out.filter((a) => a.tags.length)
  // Ordinary prose edits don't change ratings. Keep the reference stable so
  // callers can skip state updates without serializing all annotation anchors.
  return out.length === annotations.length &&
    out.every(
      (a, i) =>
        a.id === annotations[i].id &&
        a.tags.length === annotations[i].tags.length &&
        a.tags.every((tag, j) => tag === annotations[i].tags[j]),
    )
    ? annotations
    : out
}

/** Preserve all title formatting. Only replace the prefix, not the whole heading. */
export function setHeadingReadiness(heading: HeadingBlock, readiness?: number): HeadingBlock {
  const raw = getBlockText(heading)
  const opening = parseSectionOpening(raw)
  if (!opening) {
    return heading
  }
  const length = raw.indexOf(opening.title)
  let skip = length
  const content = heading.content.flatMap((node) => {
    if (!skip) {
      return [node]
    }
    if (node.type !== 'text') {
      throw new Error('Section prefix must be plain text')
    }
    if (node.text.length <= skip) {
      skip -= node.text.length
      return []
    }
    const rest = { ...node, text: node.text.slice(skip) }
    skip = 0
    return [rest]
  })
  return { ...heading, content: [{ type: 'text', text: `^${readiness ?? ''} ` }, ...content] }
}

/** Existing rating controls change the same prefix; unrelated tag edits leave it alone. */
export function applyReadinessAnnotationChanges<T extends RatingAnnotation>(
  ast: ContentAST,
  before: T[],
  after: T[],
): ContentAST {
  return {
    ...ast,
    blocks: ast.blocks.map((block) => {
      if (block.type !== 'heading' || !parseSectionOpening(getBlockText(block))) {
        return block
      }
      const tags = (items: T[]) =>
        items.filter((a) => a.anchor.type === 'block' && a.anchor.blockId === block.id).flatMap((a) => a.tags)
      const oldRating = readinessFromTags(tags(before)),
        newRating = readinessFromTags(tags(after))
      return oldRating === newRating || parseSectionOpening(getBlockText(block))?.readiness === newRating
        ? block
        : setHeadingReadiness(block, newRating)
    }),
  }
}
